import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HomePage from "layouts/HomePage";
import HomePageButton from "components/HomePageButton";
import StructureModal from "components/StructureModal";
import ModeToggle from "components/ModeToggle";
import ProgrammingButtons from "components/ProgrammingButtons";
import "../assets/css/argon-dashboard-react.css";
import "../assets/css/Home.css";
import { structure } from "../assets/globalHelpers";
import { useEffect } from "react";
import { getCookie, getCsrfToken } from "../apiHandles/user_authentication"
import axiosInstance from "apiHandles/axiosInstance";
import { Button, Container, Row, Col } from "reactstrap";
import { useLanguage } from "context/LanguageProvider";
import CryptoJS from 'crypto-js';
import { useLocation } from "react-router-dom";

const SECRET_KEY = process.env.REACT_APP_STRIPE_ENCRYPTION_SECRET_KEY
const programmingButtonsNames = ["Python"]

function HomePageView() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [allStructures, setAllStructures] = useState([]);
  const [success, setSuccess] = useState(false);
  const [structureSaves, setStructureSaves] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [helper, setHelper] = useState("")
  const [bottomMargin, setBottomMargin] = useState("5%");
  const [loadingSubstructures, setLoadingSubstructures] = useState(false);
  const location = useLocation();
  const [isProgramming, setIsProgramming] = useState(() => {
    if (location.state?.isProgramming !== undefined) {
      return location.state.isProgramming;
    }
    return localStorage.getItem("homeMode") === "programming";
  });

  const { selLanguage, toggleLanguage, IsLoading } = useLanguage();

  useEffect(() => {
    const updateMargin = () => {
      const height = window.innerHeight;
      if (height < 700) {
        setBottomMargin("60px");
      } else if (height < 900) {
        setBottomMargin("80px");
      } else {
        setBottomMargin("100px");
      }
    };

    updateMargin();
    window.addEventListener("resize", updateMargin);
    return () => window.removeEventListener("resize", updateMargin);
  }, []);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("access") && localStorage.getItem("access") !== "true";
    if (!isLoggedIn) {
      navigate("/auth/login");
    }
  }, [navigate]);

  const fetchStructureData = async () => {
    try {
      const response = await axiosInstance.get("/api/structure/");
      setAllStructures(response.data);
      setSuccess(true);
    } catch (error) {
      if (error.response?.status === 401) {
        alert(selLanguage ? "Πρέπει να κάνεις login ξανά" : "You need to login again");
        localStorage.removeItem("access");
        navigate("/login");
      } else if (error.response?.status === 403) {
        alert(selLanguage ? "CSRF error. Δοκίμασε ξανά." : "CSRF error. Please try again.");
      } else {
        alert("PROBLEM: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubstructuresForStructure = async (structureId) => {
    try {
      setLoadingSubstructures(true);

      const cacheKey = `structure_subs_${structureId}`;
      const cached = sessionStorage.getItem(cacheKey);

      if (cached) {
        return JSON.parse(cached);
      }

      const response = await axiosInstance.get(
        `/api/structure/${structureId}/substructures/`
      );

      sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      return response.data;

    } catch (error) {
      console.error("Error fetching substructures:", error);
      if (error.response?.status === 401) {
        alert(selLanguage ? "Πρέπει να κάνεις login ξανά" : "You need to login again");
        localStorage.removeItem("access");
        navigate("/login");
      } else if (error.response?.status === 403) {
        alert("Δεν έχεις δικαίωμα πρόσβασης σε αυτά τα δεδομένα");
      } else {
        alert("Σφάλμα κατά τη φόρτωση substructures: " + error.message);
      }
      return null;
    } finally {
      setLoadingSubstructures(false);
    }
  };

  function decryptData(encryptedData) {
    try {
      const key = CryptoJS.SHA256(SECRET_KEY);
      const decrypted = CryptoJS.AES.decrypt(
        encryptedData,
        key,
        {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7
        }
      );
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error("Decryption failed:", error);
      return null;
    }
  }

  const fetchStructureSaveData = async () => {
    try {
      const response = await axiosInstance.get("/user/structuresave/");
      const decryptedData = decryptData(response.data.data);
      setStructureSaves(decryptedData);
    } catch (error) {
      if (error.response?.status === 401) {
        alert(selLanguage ? "Πρέπει να κάνεις login ξανά" : "You need to login again");
        localStorage.removeItem("access");
        navigate("/login");
      } else {
        alert("Σφάλμα: " + error.message);
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchStructureData();
      await fetchStructureSaveData();
    };
    loadData();
  }, []);

  useEffect(() => {
    const cameFromLink = localStorage.getItem("cameFromLink");

    if (cameFromLink === "true" && !isLoading && allStructures.length > 0) {
      localStorage.removeItem("cameFromLink");
      const currentStructure = localStorage.getItem("structureLocation");

      if (currentStructure) {
        const structure = allStructures.find(s => s.name === currentStructure);
        if (structure) {
          handleStructureClick(structure.name, structure.id);
        }
      }
    }
  }, [allStructures, isLoading]);

  const customOrder = {
    graph: 0,
    tree: 1,
    list: 2,
    stack: 5,
    queue: 4,
    set: 7,
    array: 3,
    hashtable: 6
  };

  const mainButtons = success && allStructures.length > 0
    ? [...allStructures]
      .sort((a, b) => {
        const orderA = customOrder[a.name.toLowerCase()] ?? Infinity;
        const orderB = customOrder[b.name.toLowerCase()] ?? Infinity;
        return orderA - orderB;
      })
      .map((structure) => {
        return {
          label: structure.name,
          id: structure.id,
          path: "/ds",
        };
      })
    : [];

  const handleStructureClick = async (structureName, structureId) => {
    try {
      const structureData = await fetchSubstructuresForStructure(structureId);

      if (structureData && structureData.substructures) {
        const subcategories = structureData.substructures.length > 0
          ? structureData.substructures.map((subcategory) => {
            return {
              ...subcategory
            };
          })
          : [{ name: "No substructures available", progress: 0 }];

        setSubcategories(subcategories);
        setHelper(structureName);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error("Error handling structure click:", error);
    }
  };

  // Handler for programming buttons — looks up the structure by name then reuses handleStructureClick
  const handleProgrammingButtonClick = (name) => {
    const structure = allStructures.find(s => s.name === name);
    if (structure) {
      localStorage.setItem("structureLocation", structure.name);
      handleStructureClick(structure.name, structure.id);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  function getProgress(structureId) {
    const match = structureSaves.find(s => s.structure_id === structureId);
    return match?.progression ?? 0;
  }

  return (
    <HomePage>

      <ModeToggle
        isProgramming={isProgramming}
        onToggle={() => {
          const next = !isProgramming;
          setIsProgramming(next);
          localStorage.setItem("homeMode", next ? "programming" : "ds");
        }}
      />


      <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "top", width: "100%" }}>
        {isLoading ? (
          <div>Loading structures...</div>
        ) : (
          <>
            {!isProgramming ? (
              <div className="buttons-container">
                <Row className="buttons-rowGrid">
                  {mainButtons.map((btn, index) =>
                    !programmingButtonsNames.includes(btn.label) && (
                      <Col
                        key={index}
                        xl="12"
                        lg="12"
                        md="12"
                        sm="12"
                        className="mb-4"
                      >
                        <HomePageButton
                          label={btn.label}
                          onClick={() => {
                            const structure = btn.label;
                            localStorage.setItem("structureLocation", structure);
                            handleStructureClick(btn.label, btn.id);
                          }}
                          showProgress={true}
                          progress={getProgress(btn.id) || 0}
                          disabled={loadingSubstructures}
                        />
                      </Col>
                    )
                  )}
                </Row>
              </div>
            ) : (
              <ProgrammingButtons
                structures={allStructures.filter(s => programmingButtonsNames.includes(s.name))}
                onButtonClick={handleProgrammingButtonClick}
                getProgress={getProgress}
              />
            )}
          </>
        )}
      </div>

      {loadingSubstructures && (
        <div className="loading-overlay">
          <div>Loading substructures...</div>
        </div>
      )}

      {/* Single StructureModal shared by both DS and Programming buttons */}
      <StructureModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        structureName={helper}
        subcategories={subcategories}
        structures={allStructures}
      />
    </HomePage>
  );
}

export default HomePageView;